import { T, Var } from 'gt-react';
import { useLocalStorageValue } from '@react-hookz/web';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { ARInvoiceMetadata } from './ar-invoice-metadata';
import { ARInvoiceLineItems } from './ar-invoice-line-items';
import { ARInvoiceInvoiceLines } from './ar-invoice-invoice-lines';
import { ARInvoicePaymentHistory } from './ar-invoice-payment-history';
import { ARInvoiceEmails } from './ar-invoice-activity';
import type { ArInvoiceDetail } from '@erp/rpc';

interface ARInvoiceDetailsProps {
  invoice: ArInvoiceDetail;
  /**
   * Leaves out the Activity / Payment History / Emails pane, for a page that
   * shows its own notes and activity (the AR task detail).
   */
  hideActivity?: boolean;
  /**
   * Opens on the invoice's billed lines (when it has any) instead of its
   * order's lines, for a page about what the customer was billed.
   */
  preferInvoiceLines?: boolean;
}

/** The invoice's metadata, then its order lines and billed lines as tabs. */
function ARInvoiceData({
  invoice,
  preferInvoiceLines,
}: {
  invoice: ArInvoiceDetail;
  preferInvoiceLines: boolean;
}) {
  const lineItems = invoice.salesOrder?.lineItems ?? [];
  const invoiceLineItems = invoice.invoiceLineItems ?? [];
  const hasInvoiceLines = invoiceLineItems.length > 0;
  const leftDefaultTab =
    hasInvoiceLines && (preferInvoiceLines || lineItems.length === 0)
      ? 'invoice-lines'
      : 'line-items';

  return (
    <>
      <div className="shrink-0">
        <ARInvoiceMetadata invoice={invoice} />
      </div>

      <Separator />

      <Tabs defaultValue={leftDefaultTab} className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="line-items">
            <T context="tab label for invoice line items">
              Line Items (<Var>{lineItems.length}</Var>)
            </T>
          </TabsTrigger>
          {hasInvoiceLines && (
            <TabsTrigger value="invoice-lines">
              <T context="tab label for invoice lines">
                Invoice Lines (<Var>{invoiceLineItems.length}</Var>)
              </T>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="line-items" className="mt-0">
          <ARInvoiceLineItems
            lineItems={lineItems}
            currency={invoice.currencyCode}
          />
        </TabsContent>

        {hasInvoiceLines && (
          <TabsContent value="invoice-lines" className="mt-0">
            <ARInvoiceInvoiceLines
              invoiceLineItems={invoiceLineItems}
              currency={invoice.currencyCode}
            />
          </TabsContent>
        )}
      </Tabs>
    </>
  );
}

export function ARInvoiceDetails({
  invoice,
  hideActivity = false,
  preferInvoiceLines = false,
}: ARInvoiceDetailsProps) {
  const paymentApplications = invoice.paymentApplications ?? [];
  const inboundEmails = invoice.inboundEmails ?? [];
  const outboundEmails = invoice.outboundEmails ?? [];
  const uniqueConversationCount = new Set(
    inboundEmails.map((e) =>
      e.conversationId ? `thread:${e.conversationId}` : `email:${e.id}`,
    ),
  ).size;
  const emailCount = uniqueConversationCount + outboundEmails.length;
  const invoiceEvents = invoice.invoiceEvents ?? [];

  const rightDefaultTab =
    invoiceEvents.length > 0
      ? 'activity'
      : paymentApplications.length > 0
        ? 'payment-history'
        : 'emails';

  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'ar-invoice-details-panel-size',
    { defaultValue: 60, initializeWithValue: true },
  );

  if (hideActivity) {
    return (
      <div className="space-y-4 px-3">
        <ARInvoiceData
          invoice={invoice}
          preferInvoiceLines={preferInvoiceLines}
        />
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      {/* Left panel: metadata + invoice data tabs */}
      <ResizablePanel
        defaultSize={panelSize}
        onResize={(size) => setPanelSize(size)}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <ARInvoiceData
            invoice={invoice}
            preferInvoiceLines={preferInvoiceLines}
          />
        </div>
      </ResizablePanel>

      <ResizableHandle />

      {/* Right panel: payment history + activity */}
      <ResizablePanel minSize={20}>
        <Tabs defaultValue={rightDefaultTab} className="flex flex-col h-full">
          <TabsList className="w-full shrink-0">
            <TabsTrigger value="activity">
              <T context="tab label for invoice activity log">
                Activity (<Var>{invoiceEvents.length}</Var>)
              </T>
            </TabsTrigger>
            <TabsTrigger value="payment-history">
              <T context="tab label for payment history">
                Payment History (<Var>{paymentApplications.length}</Var>)
              </T>
            </TabsTrigger>
            <TabsTrigger value="emails">
              <T context="tab label for invoice emails">
                Emails (<Var>{emailCount}</Var>)
              </T>
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="activity"
            className="mt-0 flex-1 min-h-0 overflow-y-auto"
          >
            {invoiceEvents.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                <T>No activity</T>
              </div>
            ) : (
              <ul className="space-y-2 p-3">
                {invoiceEvents.map((event) => (
                  <li key={event.id} className="bg-muted/50 rounded px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium capitalize">
                        {event.eventType.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(event.occurredAt).toLocaleDateString()}
                      </span>
                    </div>
                    {event.note && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {event.note}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent
            value="payment-history"
            className="mt-0 flex-1 min-h-0 overflow-y-auto"
          >
            <ARInvoicePaymentHistory
              paymentApplications={paymentApplications}
              currency={invoice.currencyCode}
            />
          </TabsContent>

          <TabsContent
            value="emails"
            className="mt-0 flex-1 min-h-0 overflow-y-auto"
          >
            <ARInvoiceEmails
              inboundEmails={inboundEmails}
              outboundEmails={outboundEmails}
            />
          </TabsContent>
        </Tabs>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
